import {
  DealPipelineInvestmentWorkflowStateFiltersQuery,
  DueDiligenceRecordStatus,
  RecordStatusDialogInvestmentQuery
} from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import RadioButton from '@/components/basicComponents/radioButton';
import { UPDATE_INVESTMENT_SECURITY_STATUS_TRANSITION_MUTATION } from '@/components/dealPipeline-page/queries';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { startCase } from 'lodash';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';

const getStrategyStatusModalConfig = (recordStatus: DueDiligenceRecordStatus, currentStrategyStatus: string) => {
  if (recordStatus === DueDiligenceRecordStatus.Approved) {
    return {
      title: 'Approve Due Diligence',
      defaultStatus: 'Recommended',
      description: (
        <>
          Do you want to update this strategy to <b>Recommended</b> or leave it set to <b>{currentStrategyStatus}</b>?
        </>
      )
    };
  }

  if (recordStatus === DueDiligenceRecordStatus.Rejected) {
    return {
      title: 'Reject Due Diligence',
      defaultStatus: 'Rejected',
      description: (
        <>
          Do you want to update this strategy to <b>Rejected</b> or leave it set to <b>{currentStrategyStatus}</b>?
        </>
      )
    };
  }

  if (recordStatus === DueDiligenceRecordStatus.Active) {
    return {
      title: 'Reopen Due Diligence',
      defaultStatus: 'Due Diligence',
      description: (
        <>
          Do you want to update this strategy to <b>Due Diligence</b> or leave it set to <b>{currentStrategyStatus}</b>?
        </>
      )
    };
  }

  return {
    title: '',
    defaultStatus: '',
    description: <></>
  };
};

const workflowStatesMap: Record<string, string> = {
  Recommended: 'recommended',
  Rejected: 'rejected',
  'Due Diligence': 'dueDiligence'
};

interface StrategyStatusProps {
  isOpen: boolean;
  recordStatus: DueDiligenceRecordStatus;
  workflowStates: DealPipelineInvestmentWorkflowStateFiltersQuery['InvestmentWorkflowStateFilters'];
  investmentData?: RecordStatusDialogInvestmentQuery['Investment'];
  handleUpdateRecordStatus: ({ skipUpdateWorkflowState }: { skipUpdateWorkflowState?: boolean }) => Promise<void>;
  onClose: () => void;
}

const StrategyStatus = ({ isOpen, recordStatus, workflowStates, investmentData, handleUpdateRecordStatus, onClose }: StrategyStatusProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();
  const { control, setValue, handleSubmit } = useForm<{ status: string }>({ defaultValues: { status: '' } });

  const [radioButtonsOptions, setRadioButtonsOptions] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [updateInvestmentSecurityStatusTransition] = useMutation(UPDATE_INVESTMENT_SECURITY_STATUS_TRANSITION_MUTATION);

  const { title, defaultStatus, description } = getStrategyStatusModalConfig(
    recordStatus,
    startCase(investmentData?.workflowInstance?.currentState?.label)
  );

  useEffect(() => {
    if (!investmentData) return;

    setValue('status', defaultStatus);
    setRadioButtonsOptions([defaultStatus, `${startCase(investmentData?.workflowInstance?.currentState?.label)} (current)`]);
  }, [investmentData, defaultStatus]);

  useEffect(() => {
    const { currentState } = investmentData?.workflowInstance || {};
    if (recordStatus === DueDiligenceRecordStatus.Active && (currentState?.name === 'recommended' || currentState?.name === 'rejected')) {
      handleConfirmStrategyStatus({ status: 'Due Diligence' });
    }
  }, [recordStatus, investmentData]);

  const handleConfirmStrategyStatus = async (data: { status: string }) => {
    try {
      setErrorMessage(null);
      setLoading(true);

      if (data.status === defaultStatus) {
        const workflowState = workflowStates.find((state) => state.name === workflowStatesMap[data.status]);
        if (workflowState) {
          await updateInvestmentSecurityStatusTransition({
            variables: {
              data: {
                investmentId: investmentData?.id,
                rawInput: { destinationStateId: workflowState.id }
              }
            }
          });
        }
      }

      await handleUpdateRecordStatus({ skipUpdateWorkflowState: true });
    } catch {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      confirmButton={{
        name: 'Confirm',
        onClick: handleSubmit(handleConfirmStrategyStatus),
        disabled: loading
      }}
      showCancelButton={!loading}
      showCloseIcon={!loading}
      isUseOutsideClick={!loading}
    >
      {loading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <Banner
              icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
              title="Error"
              description={errorMessage}
              bgColor={themeVar('context.error')}
            />
          )}
          <Subtitle>{description}</Subtitle>
          <Controller
            name="status"
            control={control}
            render={({ field: { onChange, value } }) => (
              <RadioButtonsWrapper>
                {radioButtonsOptions.map((option, index) => (
                  <RadioButton
                    key={option + index}
                    name="status"
                    radioButtonText={option}
                    selectedOption={value}
                    handleRadioChange={(event) => onChange(event.target.value)}
                  />
                ))}
              </RadioButtonsWrapper>
            )}
          />
        </>
      )}
    </ModalWrappedContent>
  );
};

export default StrategyStatus;

const Subtitle = styled.p`
  text-align: center;
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
`;

const RadioButtonsWrapper = styled.div`
  display: flex;
  padding-top: 25px;
`;
